import { commonStrings } from '../i18n';

export interface ArchivableNode {
  id: string;
  name: string;
  isArchived?: boolean | null;
}

export const archivedLabel = (name: string) =>
  `${name} (${commonStrings.archive.archived.toLowerCase()})`;

/** Every `id` string anywhere in `value`, e.g. all choices a loaded dossier references. */
export function collectIds(
  value: unknown,
  ids: Set<string> = new Set(),
): Set<string> {
  if (Array.isArray(value)) {
    for (const item of value) collectIds(item, ids);
  } else if (value && typeof value === 'object' && !(value instanceof Date)) {
    for (const [key, item] of Object.entries(value)) {
      if (key === 'id' && typeof item === 'string') ids.add(item);
      else collectIds(item, ids);
    }
  }
  return ids;
}

/**
 * Options a form may offer: active nodes plus archived ones still in use,
 * the latter labelled "(archiviert)" so they stand out.
 */
export function selectableNodes<T extends ArchivableNode>(
  nodes: ReadonlyArray<T> | null | undefined,
  inUse: ReadonlySet<string>,
): T[] {
  const result: T[] = [];
  for (const node of nodes ?? []) {
    if (!node.isArchived) result.push(node);
    else if (inUse.has(node.id))
      result.push({ ...node, name: archivedLabel(node.name) });
  }
  return result;
}

export function toArchivedNames(
  ...lists: ReadonlyArray<ReadonlyArray<ArchivableNode> | null | undefined>
): Map<string, string> {
  const names = new Map<string, string>();
  for (const list of lists) {
    for (const node of list ?? []) names.set(node.id, node.name);
  }
  return names;
}

/** Names of archived values still referenced by `value`, in first-seen order. */
export function findArchivedSelections(
  value: unknown,
  archived: ReadonlyMap<string, string>,
): string[] {
  const names = new Set<string>();
  for (const id of collectIds(value)) {
    const name = archived.get(id);
    if (name) names.add(name);
  }
  return [...names];
}
